import { BALANCE } from './balance';
import type { Millis, Timestamp } from './types';

/**
 * Client/server clock synchronisation - the pure maths half. The transport
 * lives in `client/src/net/SyncedClock.ts`; everything decidable without a
 * socket is here so it can be unit-tested and reused.
 *
 * Why this exists at all: every phase boundary in this game is announced as an
 * **absolute server timestamp** ("inputs open at 1731959281204"), never as a
 * duration ("inputs open in 3000ms"). A chain of `setTimeout`s on the client
 * drifts, pauses when the tab is backgrounded, and puts two players in a versus
 * match seconds apart. An absolute deadline plus a known offset does not.
 *
 * To use an absolute deadline the client needs to know how far its own clock is
 * from the server's. That is `offset`:
 *
 *     serverNow = clientNow + offset
 */

export interface ClockSample {
  /** Round trip time for this probe. */
  readonly rtt: Millis;
  /** serverNow - clientNow, as estimated by this single probe. */
  readonly offset: Millis;
  /** Client time the sample was completed at. */
  readonly at: Timestamp;
}

export interface ClockEstimate {
  /** Add to a client timestamp to get a server timestamp. */
  readonly offset: Millis;
  /** Median round trip of the samples that were kept. */
  readonly rtt: Millis;
  /** Spread of the kept round trips. High jitter means a low-confidence offset. */
  readonly jitter: Millis;
  /** How many samples survived filtering. */
  readonly sampleCount: number;
}

/**
 * One probe.
 *
 * Assumes the trip out and the trip back take the same time, so the server's
 * clock at the moment we received the reply was `serverTime + rtt/2`. That
 * assumption is wrong on asymmetric links, which is exactly why we keep the
 * lowest-RTT samples: the shorter the trip, the less room for asymmetry.
 */
export function sampleRoundTrip(
  clientSentAt: Timestamp,
  serverTime: Timestamp,
  clientReceivedAt: Timestamp,
): ClockSample {
  const rtt = clientReceivedAt - clientSentAt;
  return {
    rtt,
    offset: serverTime + rtt / 2 - clientReceivedAt,
    at: clientReceivedAt,
  };
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1
    ? (sorted[mid] as number)
    : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

export interface ReduceOptions {
  readonly keepBestSamples?: number;
  readonly maxUsableRttMs?: number;
}

/**
 * Turn a pile of probes into one estimate.
 *
 * Median rather than mean: a single stalled packet skews a mean badly, and
 * mobile networks produce those constantly. Discarding the slow half of the
 * samples first is the standard NTP-style trick, for the same reason.
 *
 * Returns null when nothing usable survived - the caller keeps its previous
 * estimate rather than trusting garbage.
 */
export function reduceSamples(
  samples: readonly ClockSample[],
  options: ReduceOptions = {},
): ClockEstimate | null {
  const maxRtt = options.maxUsableRttMs ?? BALANCE.clock.maxUsableRttMs;
  const keep = options.keepBestSamples ?? BALANCE.clock.keepBestSamples;

  const usable = samples.filter((s) => s.rtt >= 0 && s.rtt <= maxRtt);
  if (usable.length === 0) return null;

  const best = [...usable].sort((a, b) => a.rtt - b.rtt).slice(0, Math.max(1, keep));
  const rtts = best.map((s) => s.rtt);

  return {
    offset: Math.round(median(best.map((s) => s.offset))),
    rtt: Math.round(median(rtts)),
    jitter: Math.round(Math.max(...rtts) - Math.min(...rtts)),
    sampleCount: best.length,
  };
}

/**
 * Blend a new estimate into the current one.
 *
 * A big correction is applied at once - we were simply wrong. A small one is
 * eased in, because snapping the clock by a few milliseconds mid-animation is
 * visible as a stutter and is never worth it.
 */
export function applyEstimate(
  current: ClockEstimate | null,
  next: ClockEstimate,
  slewThresholdMs: Millis = BALANCE.clock.slewThresholdMs,
): ClockEstimate {
  if (!current) return next;
  const delta = next.offset - current.offset;
  if (Math.abs(delta) >= slewThresholdMs) return next;
  return { ...next, offset: Math.round(current.offset + delta * 0.25) };
}
